import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { isEqual } from 'lodash-es';
import mapboxgl, { Popup as PopupInstanceBase, PopupOptions } from 'mapbox-gl';

import ActionsCancel from '@strava/icons/ActionsCancelNormalXsmall';

import { Button } from '../../Button/Button';
import { useMap } from '../hooks/useMap';
import { LatLng } from '../types';
import { getClassNameSet } from '../utils';

import styles from './Popup.module.scss';

export interface PopupEvent {
  type: 'open' | 'close';
  target: PopupInstance;
}

export type PopupEventListener = (e: PopupEvent) => void;

export type PopupInstance = PopupInstanceBase;

export interface PopupProps extends PopupOptions {
  /**
   * Coordinates of the marker.
   */
  latLng: LatLng;

  /**
   * Called when popup is open manually or programmatically.
   */
  onOpen?: PopupEventListener;

  /**
   * Called when popup is closed manually or programmatically.
   */
  onClose?: PopupEventListener;

  /**
   * Closes popup when something other than the popup or map is clicked. Also
   * allows the escape key to close the popup.
   */
  closeOnOutsideClick?: boolean;

  children?: React.ReactNode;

  /**
   * If true, the map will automatically pan the popup into view when it is
   * opened.
   */
  autoPan?: boolean;

  /**
   * custom padding for the autoPan to avoid colliding with other elements on the page;
   * default padding is 32px
   */
  autoPanPadding?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
}

export const Popup = forwardRef<PopupInstance, PopupProps>((props, ref) => {
  const {
    className,
    latLng,
    children,
    closeOnOutsideClick = false,
    autoPan = false,
    autoPanPadding,
    ...mapboxPopupOptions
  } = props;
  const map = useMap();

  const prevPropsRef = useRef<PopupProps>(props);
  const classNameRef = useRef<string>();

  // container used for the portal that will render React components inside of mapboxgl.Popup
  const container = useMemo(() => document.createElement('div'), []);

  // Create mapbox popup instance
  const popup = useMemo(
    () => {
      const popupInstance = new mapboxgl.Popup({
        ...mapboxPopupOptions,
        // If autoPan is enabled, set anchor to bottom as the autoPan logic
        // assumes the anchor is at the bottom of the popup.
        anchor: autoPan ? 'bottom' : mapboxPopupOptions.anchor,
        className: clsx(styles.popup, className),
        // Hide default close button in favor of our custom button with Strava icon
        closeButton: false
      }) as PopupInstance;

      popupInstance.setLngLat([latLng[1], latLng[0]]);

      return popupInstance;
    },
    // As we only want to create mapboxgl.Popup once and as we manually update it's props/options,
    // we intentionally have empty dependency array and disabled the eslint rule here.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useImperativeHandle(ref, () => popup, [popup]);

  const handleAutoPan = () => {
    if (!map) {
      return;
    }
    const DEFAULT_PADDING = 32;

    const padding = {
      top: autoPanPadding?.top ?? DEFAULT_PADDING,
      bottom: autoPanPadding?.bottom ?? DEFAULT_PADDING,
      left: autoPanPadding?.left ?? DEFAULT_PADDING,
      right: autoPanPadding?.right ?? DEFAULT_PADDING
    };

    // Get map position on the page
    const {
      left: mapLeft,
      right: mapRight,
      top: mapTop,
      bottom: mapBottom
    } = map.getContainer().getBoundingClientRect();

    // Get popup position on the page
    const {
      left: popupLeft,
      right: popupRight,
      top: popupTop,
      bottom: popupBottom
    } = popup.getElement().getBoundingClientRect();

    // Calculate relative position of popup on the map by subtracting
    // map position from popup position
    const left = popupLeft - mapLeft;
    const right = popupRight - mapRight;
    const top = popupTop - mapTop;
    const bottom = popupBottom - mapBottom;

    const newLeft = left < 0 ? left - padding.left : undefined;
    const newRight = right > 0 ? right + padding.right : undefined;
    const newTop = top < mapTop ? top - padding.top : undefined;
    const newBottom = bottom > 0 ? bottom + padding.bottom : undefined;

    const x = newLeft ?? newRight ?? 0;
    const y = newTop ?? newBottom ?? 0;

    if (x !== 0 || y !== 0) {
      map.panBy([x, y], { essential: true });
    }
  };

  useEffect(
    () => {
      const handleOpen = (e?: object) => {
        prevPropsRef.current.onOpen?.(e as PopupEvent);
      };
      const handleClose = (e?: object) => {
        prevPropsRef.current.onClose?.(e as PopupEvent);
      };

      popup.on('open', handleOpen);
      popup.on('close', handleClose);
      popup.setDOMContent(container);

      if (map) {
        popup.addTo(map);

        if (autoPan) {
          handleAutoPan();
        }
      }

      return () => {
        popup.off('open', handleOpen);
        popup.off('close', handleClose);
        popup.remove();
      };
    },
    // We only want to attach events once on initial render and as popup will not change in the
    // lifespan of this component we intentionally have empty dependency array and disabled the
    // eslint rule here.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    const handleOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as HTMLElement;
      const popupElem = popup.getElement();

      if (
        !popupElem ||
        popupElem.contains(target) ||
        target.classList.contains('mapboxgl-canvas')
      ) {
        return;
      }

      popup.fire('close');
    };

    //
    // Handle closing popup on ESC key press
    //
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') {
        return;
      }

      popup.fire('close');
    };

    if (closeOnOutsideClick) {
      document.addEventListener('mousedown', handleOutside);
      document.addEventListener('touchstart', handleOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeOnOutsideClick]);

  //
  // Update the className property
  //
  if (classNameRef.current !== className) {
    const prevClassNames = getClassNameSet(classNameRef.current);
    const newClassNames = getClassNameSet(clsx(styles.popup, className));

    prevClassNames.forEach((c) => {
      if (!newClassNames.has(c)) {
        popup.removeClassName(c);
      }
    });

    newClassNames.forEach((c) => {
      if (!prevClassNames.has(c)) {
        popup.addClassName(c);
      }
    });

    classNameRef.current = className;
  }

  //
  // Update the geographical location of the anchor property, and moves the popup to it.
  //
  const [lat, lng] = latLng;
  if (popup.getLngLat().lat !== lat || popup.getLngLat().lng !== lng) {
    popup.setLngLat([latLng[1], latLng[0]]);

    if (autoPan) {
      handleAutoPan();
    }
  }

  //
  // Update the maximum width property. This is setting the CSS property max-width.
  //
  if (
    mapboxPopupOptions.maxWidth !== undefined &&
    popup.getMaxWidth() !== mapboxPopupOptions.maxWidth
  ) {
    popup.setMaxWidth(mapboxPopupOptions.maxWidth);
  }

  //
  // Update the offset property
  //
  if (!isEqual(prevPropsRef.current.offset, mapboxPopupOptions.offset)) {
    popup.setOffset(mapboxPopupOptions.offset);
  }

  // This needs to be at the bottom so that we make sure prevProps gets updates after all the checks
  // and updates have been performed.
  prevPropsRef.current = props;

  // Fragment is used here to get around React types version mismatch between @strava/ui
  // and other product apps/packages.
  return (
    <>
      {createPortal(
        <>
          {mapboxPopupOptions.closeButton && (
            <Button
              type="button"
              variant="icon"
              className={styles.closeBtn}
              // This is mimicking what mapbox-gl-js native popup does when close button is clicked
              onClick={() => popup.remove()}
            >
              <ActionsCancel height={9} width={9} />
            </Button>
          )}
          {children}
        </>,
        container
      )}
    </>
  );
});
